import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import colors from 'virtual:colors';
import { DownloadIcon, XIcon } from '@/components/icons';
import { PRODUCT_SECTIONS } from '@/pages/Products/content';
import { cn } from '@/utils/cn';

type Region = 'asia' | 'korea' | 'europe' | 'americas';
const ALL_REGIONS = ['asia', 'korea', 'europe', 'americas'] as const satisfies readonly Region[];
const isRegion = (v: string): v is Region => (ALL_REGIONS as readonly string[]).includes(v);

// Total colors available per region (the full range advertised in the
// availability line). This is intentionally not the number of best-seller
// rows shown below — those are a curated subset.
const REGION_COLOR_COUNTS: Record<Region, number> = {
  asia: 18,
  korea: 7,
  europe: 74,
  americas: 29,
};

// Downloadable catalogue per region (files live in public/catalogues/).
// Filenames — and extensions — are kept as uploaded, since Asia is a JPG
// while the others are PDFs.
const REGION_CATALOGUE: Record<Region, string> = {
  asia: '/catalogues/Asia_Color_Chart.jpg',
  korea: '/catalogues/Korea_Color_Chart.pdf',
  europe: '/catalogues/Europe_Color_Chart.pdf',
  americas: '/catalogues/2026_Web_CC_Chart_US_Final_2.pdf',
};
const GLOBAL_CATALOGUE = '/catalogues/Global_Color_Chart.pdf';

const slugify = (s: string) => s.toLowerCase().trim().replace(/\s+/g, '-');

interface ColorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Color range modal — opened from the "View colors" CTA inside any color
 * family product's spec panel.
 *
 * Variants are global by region (not scoped to the clicked product) — the
 * user can compare every available color in their region at once. Each
 * variant row shows the parent product's name so the source lens is clear.
 *
 * Variant data comes from `virtual:colors` (sourced from
 * `content/colors/colors.xlsx` — see the README in that folder). Filtering
 * by region matches the active tab against the row's comma-separated `region`
 * field, case-insensitively.
 *
 * Tab interactions:
 * - The active-tab underline is a single absolutely-positioned element that
 *   slides between tab positions (transform: translateX) on every change.
 * - The body content keys off `activeRegion`, so each tab change replays a
 *   slide-in animation. Direction is determined by the tab order: moving to
 *   a later tab slides in from the right, moving back slides in from the left.
 */
export function ColorModal({ isOpen, onClose }: ColorModalProps) {
  const { t, i18n } = useTranslation();
  // Tab order follows the active language — same array used by the contact
  // form's region dropdown — so the language's own region surfaces first.
  const REGIONS = useMemo<readonly Region[]>(() => {
    const raw = t('contact.form.regionOrder', { returnObjects: true }) as string[];
    return raw.filter(isRegion);
    // Re-derive when the language changes; `t` is stable but
    // `resolvedLanguage` is the actual signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);
  const [activeRegion, setActiveRegion] = useState<Region>(REGIONS[0] ?? 'asia');
  // Direction is captured at the moment of the tab click, then read by the
  // re-keyed body to pick its slide-in animation.
  const [slideDirection, setSlideDirection] = useState<'right' | 'left'>('right');
  const prevIndexRef = useRef(0);

  // Reset to the language's first ordered region whenever the language
  // flips or the modal is (re)opened — so each open starts at index 0
  // and stays in sync with the tab strip's leading position.
  useEffect(() => {
    if (REGIONS.length === 0) return;
    setActiveRegion(REGIONS[0]);
    prevIndexRef.current = 0;
  }, [REGIONS, isOpen]);

  // Flat lookups derived from PRODUCT_SECTIONS:
  // - `productNameById`: variant rows show the parent product's display name.
  // - `productOrderById`: variants sort within the modal by the catalogue's
  //   own product ordering (clearcolor 1-day → clearcolor → phantom), so the
  //   list always reads top-down by product even though the xlsx may be in
  //   any order. Unknown product ids fall back to phantom-or-end placement.
  const { productNameById, productOrderById } = useMemo(() => {
    const names = new Map<string, string>();
    const orders = new Map<string, number>();
    let i = 0;
    for (const section of PRODUCT_SECTIONS) {
      for (const group of section.groups) {
        for (const product of group.products) {
          names.set(product.id, product.name);
          orders.set(product.id, i++);
        }
      }
    }
    return { productNameById: names, productOrderById: orders };
  }, []);

  const productSortKey = (productId: string): number => {
    const idx = productOrderById.get(productId);
    if (idx !== undefined) return idx;
    // Unknown id (e.g. an xlsx-only "clearcolor-phantom") — group with the
    // phantom slot so it doesn't drift to the end of the list.
    if (productId.toLowerCase().includes('phantom')) {
      return productOrderById.get('phantom') ?? Number.POSITIVE_INFINITY;
    }
    return Number.POSITIVE_INFINITY;
  };

  const activeIndex = REGIONS.indexOf(activeRegion);

  // Show every variant matching the region — not filtered by which product
  // launched the modal — then sort by [productSortKey, variant.order] so the
  // list reads clearcolor 1-day first, clearcolor next, phantom last.
  const variantsForRegion = colors
    .filter((c) => {
      if (!c.region) return false;
      return c.region
        .split(',')
        .map((r) => r.trim().toLowerCase())
        .includes(activeRegion.toLowerCase());
    })
    .slice()
    .sort((a, b) => {
      const ao = productSortKey(a.productId);
      const bo = productSortKey(b.productId);
      if (ao !== bo) return ao - bo;
      return a.order - b.order;
    });

  const handleTabClick = (region: Region) => {
    if (region === activeRegion) return;
    const nextIndex = REGIONS.indexOf(region);
    setSlideDirection(nextIndex > prevIndexRef.current ? 'right' : 'left');
    prevIndexRef.current = nextIndex;
    setActiveRegion(region);
  };

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Esc to close.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const cataloguePath = REGION_CATALOGUE[activeRegion];
  const variantCount = REGION_COLOR_COUNTS[activeRegion];
  const activeRegionLabel = t(`products.colorModal.tabs.${activeRegion}`);

  return createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- Backdrop click closes; keyboard close is handled by the global Escape listener above. Close button (line 156) is also focusable.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="color-modal-title"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-clearlab-divider-mid/40 backdrop-blur-[15px] md:items-center md:p-6"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Stops backdrop click; modal interaction lives on the focusable controls inside. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative flex h-full w-full flex-col bg-clearlab-oat',
          'md:h-auto md:max-h-[800px] md:w-[720px] md:rounded-lg md:border md:border-clearlab-divider-darker md:shadow-lg',
        )}
      >
        {/* Sticky top — title + region tabs */}
        <div className="flex shrink-0 flex-col gap-10 px-6 pt-6 md:px-10 md:pt-10">
          <div className="flex items-center justify-between">
            <h2
              id="color-modal-title"
              className="text-[24px] font-normal leading-6 tracking-[-0.025em] text-clearlab-midnight"
            >
              {t('products.colorModal.heading')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('products.colorModal.close')}
              className="grid size-6 place-items-center text-clearlab-midnight transition-colors duration-fast hover:text-clearlab-blue"
            >
              <XIcon className="size-6" />
            </button>
          </div>
          <div
            role="tablist"
            className="relative -mx-6 flex border-b-[1.5px] border-clearlab-divider-darker md:mx-0"
          >
            {REGIONS.map((r) => {
              const isActive = activeRegion === r;
              return (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabClick(r)}
                  className={cn(
                    'flex flex-1 items-center justify-center px-2 py-4 text-[14px] font-semibold leading-[18px] transition-colors duration-fast md:text-[16px]',
                    isActive
                      ? 'text-clearlab-midnight'
                      : 'text-clearlab-stone hover:text-clearlab-midnight',
                  )}
                >
                  {t(`products.colorModal.tabs.${r}`)}
                </button>
              );
            })}
            {/* Sliding active-tab underline. Sits on top of the divider hairline
                (-1.5px translates it down to overlap). Width is 1/N of the row
                so it covers exactly one tab; translateX(index * 100%) steps it
                across one full tab width per position. */}
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-[-1.5px] left-0 h-[1.5px] bg-clearlab-midnight transition-transform duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{
                width: `${100 / REGIONS.length}%`,
                transform: `translateX(${activeIndex * 100}%)`,
              }}
            />
          </div>
        </div>

        {/* Scrollable body — overflow-x-clip so the tab-change slide animation
            can't introduce horizontal scrollbars while transforming. */}
        <div className="flex-1 overflow-y-auto overflow-x-clip px-6 pb-10 md:px-10">
          <div
            key={activeRegion}
            className={cn(
              'flex flex-col gap-14 pt-12',
              slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left',
            )}
          >
            <div className="flex flex-col gap-10">
              <p className="text-[16px] font-normal leading-8 text-clearlab-midnight">
                <Trans
                  i18nKey="products.colorModal.availability"
                  count={variantCount}
                  values={{ count: variantCount, region: activeRegionLabel }}
                />{' '}
                {t('products.colorModal.downloadPrompt')}{' '}
                <RouterLink
                  to="/contact"
                  className="whitespace-nowrap underline underline-offset-4 transition-colors duration-fast hover:text-clearlab-blue"
                >
                  {t('products.colorModal.contactInline')}
                </RouterLink>{' '}
                {t('products.colorModal.downloadPromptSuffix')}
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 md:flex-row">
                  <a
                    href={cataloguePath}
                    download
                    onClick={(e) => e.stopPropagation()}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border-[0.7px] border-clearlab-divider-darker p-4 transition-colors duration-fast hover:bg-clearlab-divider-light/20"
                  >
                    <DownloadIcon className="size-4 text-clearlab-midnight" />
                    <span className="text-[14px] font-semibold leading-4 text-clearlab-midnight">
                      {t('products.colorModal.downloadButton', { region: activeRegionLabel })}
                    </span>
                  </a>
                  <a
                    href={GLOBAL_CATALOGUE}
                    download
                    onClick={(e) => e.stopPropagation()}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border-[0.7px] border-clearlab-divider-darker p-4 transition-colors duration-fast hover:bg-clearlab-divider-light/20"
                  >
                    <DownloadIcon className="size-4 text-clearlab-midnight" />
                    <span className="text-[14px] font-semibold leading-4 text-clearlab-midnight">
                      {t('products.colorModal.downloadGlobalButton')}
                    </span>
                  </a>
                </div>
                <p className="text-[12px] font-normal leading-4 text-clearlab-steel">
                  {t('products.colorModal.globalCatalogueNote')}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-10">
              <h3 className="text-[20px] font-semibold leading-[30px] text-clearlab-midnight">
                {t('products.colorModal.bestSellers', { region: activeRegionLabel })}
              </h3>
              {variantsForRegion.length === 0 ? (
                <p className="text-[14px] leading-5 text-clearlab-steel">
                  {t('products.colorModal.empty')}
                </p>
              ) : (
                <ul className="flex flex-col gap-12 md:gap-6">
                  {variantsForRegion.map((variant) => {
                    const productName = productNameById.get(variant.productId) ?? variant.productId;
                    // const colorNameLabel = t(
                    //   `products.colorModal.colorNames.${slugify(variant.colorName)}`,
                    //   { defaultValue: variant.colorName },
                    // );
                    const colorNameLabel =
                      variant.colorName || variant.colorCode || variant.productId;
                    const categoryLabel = variant.category
                      ? t(`products.colorModal.colorCategories.${slugify(variant.category)}`, {
                          defaultValue: variant.category,
                        })
                      : '';
                    return (
                      <li
                        key={`${variant.productId}-${variant.colorCode}`}
                        className="flex flex-col gap-4 md:flex-row md:items-center md:gap-10"
                      >
                        <div className="flex items-center gap-4 md:flex-1">
                          <div className="flex size-[100px] shrink-0 items-center justify-center rounded-lg border border-clearlab-divider-darker p-4">
                            {variant.lensImage ? (
                              <img
                                src={variant.lensImage}
                                alt=""
                                loading="lazy"
                                className="block size-full object-contain"
                              />
                            ) : null}
                          </div>
                          <div className="h-[100px] flex-1 overflow-hidden rounded-lg bg-clearlab-divider-light/20">
                            {variant.wearerImage ? (
                              <img
                                src={variant.wearerImage}
                                alt=""
                                loading="lazy"
                                className="block size-full object-cover"
                              />
                            ) : null}
                          </div>
                        </div>
                        <div className="flex flex-col gap-6 md:flex-1">
                          <div className="flex flex-col gap-2">
                            <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
                              {colorNameLabel}
                            </p>
                            <p className="flex flex-wrap items-center gap-x-3 text-[16px] font-normal leading-[18px] text-clearlab-midnight">
                              <span>{productName}</span>
                              {variant.category ? (
                                <>
                                  <span>•</span>
                                  <span>{categoryLabel}</span>
                                </>
                              ) : null}
                            </p>
                          </div>
                          <p className="text-[16px] font-normal leading-[18px] text-clearlab-steel">
                            {variant.colorCode}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
